// Copyright (c) 2026 杨翔宇（武汉文理学院）
// 物理智能教学智能体平台 (Physics Intelligent Agent)
// 保留所有权利 (All Rights Reserved)。未经著作权人书面许可，
// 不得以任何形式复制、传播、修改或用于商业用途。

import { useEffect, useMemo, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

import { useAuthStore } from '@stores/authStore';

import {
  APP_SURFACE_ATTRIBUTE,
  resolveAppSurfacePolicy,
  type AppSurfacePolicy,
} from './appSurfacePolicy';
import { AppSurfaceContext } from './useAppSurfacePolicy';

/**
 * 应用表面策略 Provider（批次性能优化 T02）。
 *
 * 位置要求（plan §3.2 约束 2）：必须放在 `main.tsx` 的 BrowserRouter/ThemeProvider 内，
 * 且**同时包住 App 与 GlassToasterLayer**——否则 Toaster 作为兄弟节点读不到页面策略。
 *
 * 为什么把标记写到 `<html>`：
 * React Portal 的 DOM 落在 `document.body`，CSS 祖先关系并不跟随 React Context；
 * 把标记放在 `<html>` 上，页面内容与所有 body Portal 都能命中同一条作用域规则，
 * 不必在每个浮层里另建一套样式。需要 Context 语义的组件仍从 `useAppSurfacePolicy` 读取，
 * 浮层宿主同时把 `data-app-surface` 写到自己的容器上（见 T04）。
 */
export function AppSurfaceProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const authBootstrapDone = useAuthStore((state) => state.authBootstrapDone);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  const policy: AppSurfacePolicy = useMemo(
    () => resolveAppSurfacePolicy({ pathname, authBootstrapDone, isAuthenticated }),
    [pathname, authBootstrapDone, isAuthenticated]
  );

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute(APP_SURFACE_ATTRIBUTE, policy.kind);
    return () => {
      // 卸载时回到既有展示体系，避免残留工作区样式影响后续渲染（例如 HMR 重挂载）。
      root.setAttribute(APP_SURFACE_ATTRIBUTE, 'existing');
    };
  }, [policy.kind]);

  return <AppSurfaceContext.Provider value={policy}>{children}</AppSurfaceContext.Provider>;
}

export default AppSurfaceProvider;
